import { useSearchParams, useNavigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { useAuth } from "@/App";
import ManagerView from "./dashboard/ManagerView";
import RepView from "./dashboard/RepView";
import AdminView from "./dashboard/AdminView";
// Iter186 (D04) — view-as sub-views are lazy so the initial /dashboard
// chunk stays small. Manager tapping "Closer" tab still gets a chunk
// download on first click; subsequent taps are instant.
const CloserDashboard = lazy(() => import("./CloserDashboard"));
const SetterDashboard = lazy(() => import("./SetterDashboard"));
const DialingDashboard = lazy(() => import("./DialingDashboard"));
const DMSetterDashboard = lazy(() => import("./DMSetterDashboard"));
import { UserSquare2, PhoneCall, Send, User } from "lucide-react";

/**
 * Role-aware Home.
 *
 * - `admin` / `superadmin` / `manager` → land on the Closer view by default
 * - anything else          → personal rep landing
 *
 * Iter185 (B02) — sidebar collapsed to one "Home" entry. Managers can
 * switch between dashboards via a pill picker at the top of this page
 * (view=closer|setter|outbound|triage|dm_setter). Reps see their role's
 * dashboard directly without the picker.
 * Iter203 — Team/Rollup/Setter TABS removed from the picker per request
 * (only Closer, Outbound, Triage show as tabs now).
 * Iter207 — default landing changed from Team overview back to Closer,
 * per explicit request ("when you press home, it goes straight to the
 * closer page").
 * Iter211 — added DM Setter tab + dedicated dashboard.
 */
const VIEW_TABS = [
  { key: "closer",    label: "Closer",    icon: UserSquare2,    hint: "Per-rep closer KPIs" },
  // Iter230 — Outbound + Triage merged into one Dialing tab.
  { key: "dialing",   label: "Dialing",   icon: PhoneCall,      hint: "Outbound dialing + inbound triage" },
  { key: "dm_setter", label: "DM Setter", icon: Send,           hint: "DM/social setter funnel" },
];
// Old bookmarks (?view=outbound / ?view=triage) land on the merged tab.
const LEGACY_VIEWS = { outbound: "dialing", triage: "dialing" };

function ViewAsPicker({ current, onChange }) {
  return (
    <div
      data-testid="home-viewas-picker"
      className="flex items-center gap-1 flex-wrap bg-[#0F1615] border border-white/5 rounded-xl p-1 w-fit mb-4"
    >
      <span className="px-2 text-[10px] uppercase tracking-wider text-gray-500 flex items-center gap-1">
        <User className="w-3 h-3" /> View as
      </span>
      {VIEW_TABS.map((tab) => {
        const Active = current === tab.key;
        const Icon = tab.icon;
        return (
          <button
            key={tab.key}
            data-testid={`home-viewas-${tab.key}`}
            onClick={() => onChange(tab.key)}
            title={tab.hint}
            className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg transition-colors ${
              Active
                ? "bg-emerald-600/20 text-emerald-300 border border-emerald-500/30"
                : "text-gray-400 hover:text-white hover:bg-white/5"
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const override = params.get("view");

  // Iter184 — normalize role casing so 'Manager' / 'ADMIN' / 'Superadmin'
  // still route to the right view.
  const role = (user?.role || "").toLowerCase();
  const isManagerTier = ["manager", "admin", "superadmin", "owner"].includes(role);

  // Resolve the view to render. Explicit `?view=` param wins; otherwise
  // pick a sensible default per role. Managers land on the team view;
  // closers/setters land on their per-rep dashboard.
  let view = LEGACY_VIEWS[override] || override;
  if (!view) {
    if (role === "admin" || role === "superadmin") view = "closer";
    else if (role === "manager") view = "closer";
    else if (role === "closer") view = "closer";
    else if (role === "setter" || role === "phone_setter") view = "setter";
    else if (role === "dm_setter") view = "dm_setter";
    else view = "rep";
  }

  const changeView = (next) => {
    if (next === "rep") {
      navigate("/dashboard", { replace: true });
      return;
    }
    const p = new URLSearchParams(params);
    p.set("view", next);
    setParams(p, { replace: true });
  };

  let body;
  switch (view) {
    case "manager":  body = <ManagerView />; break;
    case "admin":    body = <AdminView />; break;
    case "team":     body = (role === "admin" || role === "superadmin") ? <AdminView /> : <ManagerView />; break;
    case "closer":   body = <CloserDashboard />; break;
    case "setter":   body = <SetterDashboard />; break;
    case "dialing":  body = <DialingDashboard />; break;
    case "dm_setter": body = <DMSetterDashboard />; break;
    case "rep":      body = <RepView />; break;
    default:         body = <RepView />;
  }

  return (
    <div>
      {isManagerTier && <ViewAsPicker current={view} onChange={changeView} />}
      <Suspense fallback={<div className="min-h-[40vh] flex items-center justify-center text-gray-500 text-sm">Loading view…</div>}>
        {body}
      </Suspense>
    </div>
  );
}
